import { Component } from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {ManageConstraintService} from '../api/manage-constraint.service';
import {NotificationsService} from '../api/notifications.service';
import {RpcService} from '../api/rpc.service';
import {CsvExportPricesConstraint} from '../model/csv-export-prices-constraint';
import {CsvExportPricesRequest} from '../model/csv-export-prices-request';
import { EMPTY } from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';

@Component({
  selector: 'app-export-constraint',
  templateUrl: './export-constraint.component.html',
  styleUrls: ['./export-constraint.component.scss']
})
export class ExportConstraintComponent {
  form: FormGroup;
  formData: any = null;
  itemConstraintType: Array<string> = [
    'FORBIDDEN_STORE_PRICE',
  ];
  userId: string;
  permissions: string[];
  authorized_countries:  string[];
  private _pricesConstraintList:  CsvExportPricesConstraint [];
  isDownloading: boolean;
  private _csvExportPricesRequest : CsvExportPricesRequest;
  private _csvContent: string = '';

  constructor(
    private fb: FormBuilder,
    private manageConstraintService: ManageConstraintService,
    private notificationsService: NotificationsService,
    private rpcService: RpcService,
    ) {
    this.rpcService.getCurrentUser()
      .subscribe((user) => {
        this.userId = user.login;
        this.permissions = user.permissions;
        this.authorized_countries = user.authorized_countries;
      });

    this.form = this.fb.group({
      constraintType: ['', Validators.required],
      codes: ['', [Validators.required, this.codesListValidator()]],
      tierCodes: ['', [Validators.required, this.tierCodeListValidator()]],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required]
    });
  }

  tierCodeListValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || value.trim() === '') {
        return { required: true };
      }
      const tierCodes = value.split(',').map((code: string) => code.trim());

      if (tierCodes.length > 10) {
        return { maxTierCodes: true };
      }
      const isValid = tierCodes.every((code: string) => /^\d+_\d+_\d+$/.test(code));
      return isValid ? null : { invalidTierCodeList: true };
    };
  }

  codesListValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || value.trim() === '') {
        return { required: true };
      }
      const codes = value.split(',').map((code: string) => code.trim());
      if (codes.length > 500) {
        return { maxCodes: true };
      }
      return null;
    };
  }

  getElements(element : string): string[] {
    const elementList = element.split(',');
    return elementList.map(element => {
      return element.trim();
    });
  }

  formatDate = (date: string): string => {
    const d = new Date(date);
    return d.toISOString();
  };

  humanReadableDate(date: Date): String {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return !date ? '/' : new Date(date).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone : userTimeZone,
    });
  }

  submit() {
    this.isDownloading = true;
    if (this.form.valid) {
      this.formData = this.form.value;
      this._csvExportPricesRequest = {
        startDate: this.formatDate(this.formData.startDate),
        endDate: this.formatDate(this.formData.endDate),
        codes: this.getElements(this.formData.codes),
        tierCodes: this.getElements(this.formData.tierCodes),
        itemConstraintType: this.formData.constraintType,
      }

        this.manageConstraintService
          .exportItemConstraintByPeriod(this._csvExportPricesRequest)
          .subscribe((result)=> {
            this._pricesConstraintList = result;
            this.exportCsvFromList();
          }, (e)=> {
            this.handleError(e);
          });
    }
    else {
      this.notificationsService.error('Form is invalid', 'Please check your inputs.');
      this.isDownloading = false;
    }
  }

  exportCsvFromList() {
    if (!this._pricesConstraintList || this._pricesConstraintList.length === 0) {
      this.isDownloading = false;
      this.notificationsService.success('No data to export', 'The list is empty.');
      return;
    }

    this._csvContent = this._pricesConstraintList.map(item =>
      `${item.modelCode},${item.itemCode},${this.humanReadableDate(item.priceLockStartDate)},${this.humanReadableDate(item.priceLockEndDate)},${item.blockedTiersCode},${item.blockedTiersName},${item.impactedTiersCode},${item.impactedTiersName},${this.humanReadableDate(item.receptionDate)},${this.humanReadableDate(item.priceRequestStartDate)},${this.humanReadableDate(item.priceRequestEndDate)},${item.value},${item.currency},${item.priceNature},${item.user}`
    ).join('\n');

    const csvHeader = 'Model,Item,Price Lock start date,Price lock end date,Blocked tiers code,Blocked tiers name, Impacted tiers code,Impacted tiers name,Price request blocked (reception date),Start date of requested locked price,End date of requested locked price,Value,Currency,Price nature,User\n';
    const fullCsvContent = csvHeader + this._csvContent;

    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(new Blob([fullCsvContent], { type: 'text/csv;charset=utf-8;' }));
    downloadLink.download = `export-item-constraint-${new Date().toLocaleDateString()}.csv`;
    downloadLink.click();

    this.isDownloading = false;
  }

  handleError(error: HttpErrorResponse)  {
    this.notificationsService.error('Error exporting items', `${error.error?.error_description?.[0] || 'Unknown error'}`);
    this.isDownloading = false;
    return EMPTY;
  }
}
